import { Button } from "@/components/ui/UIElements/Button";
import React, { useEffect, useState } from "react";
import { NativeSelect } from "@/components/ui/UIElements/Select";
import { Modal, Input, Switch, Icon } from "../../";
import { isMyZoneForZone, typeOptions } from "./BusinessZones.utils";

import BusinessZoneCircleMap from "./BusinessZoneCircleMap";
import BusinessZonePolygonMap from "./BusinessZonePolygonMap";
import { useBusinessesLite } from "../../../../hooks";

const BusinessZoneModal = ({
  isOpen,
  onClose,
  editingZone,
  zoneForm,
  setZoneForm,
  businessAddress,
  businessLocation,
  businessId,
  onSubmitZone,
  onUpdateZoneBusinesses,
}) => {
  const [tab, setTab] = useState("details");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const { data: businesses = [], isLoading: isLoadingBusinesses } =
    useBusinessesLite({
      enabled: isOpen && tab === "share",
      searchTerm,
    });
  const [selectedBusinessIds, setSelectedBusinessIds] = useState(new Set());
  const [savingShare, setSavingShare] = useState(false);

  // Filter out the current business from the list
  const filteredBusinesses = React.useMemo(() => {
    if (!businessId) return businesses;
    return businesses.filter((b) => b.id !== parseInt(businessId, 10));
  }, [businesses, businessId]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (isOpen && tab === "share" && editingZone) {
      const current = new Set();
      (editingZone.businesses || []).forEach((b) => current.add(b.id));
      setSelectedBusinessIds(current);
    }
  }, [isOpen, tab, editingZone]);

  const toggleBusinessSelected = (id, enabled) => {
    setSelectedBusinessIds((prev) => {
      const next = new Set(prev);
      if (enabled) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    const all = new Set(filteredBusinesses.map((b) => b.id));
    setSelectedBusinessIds(all);
  };

  const handleSelectNone = () => {
    setSelectedBusinessIds(new Set());
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={editingZone ? "Edit Zone" : "Create Zone"}
      subtitle={
        editingZone ? "Update delivery zone" : "Add a new delivery zone"
      }
      maxWidth="max-w-2xl"
      className="ordering-catalog-surface"
    >
      <div className="space-y-4">
        <div className="border-border border-b">
          <nav className="flex space-x-6 overflow-x-auto">
            <Button
              variant="ghost"
              onClick={() => setTab("details")}
              className={`border-b-2 px-1 py-3 text-sm font-medium whitespace-nowrap transition-colors ${tab === "details" ? "border-primary text-foreground" : "text-muted-foreground hover:border-border hover:text-foreground border-transparent"}`}
            >
              Details
            </Button>
            <Button
              variant="ghost"
              onClick={() => setTab("share")}
              className={`border-b-2 px-1 py-3 text-sm font-medium whitespace-nowrap transition-colors ${tab === "share" ? "border-primary text-foreground" : "text-muted-foreground hover:border-border hover:text-foreground border-transparent"}`}
            >
              Share with
            </Button>
          </nav>
        </div>

        {tab === "details" ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="business-zone-name"
                  className="text-foreground mb-1 block text-sm font-medium"
                >
                  Name *
                </label>
                {(() => {
                  const isMyZone = isMyZoneForZone(editingZone);
                  return (
                    <Input
                      id="business-zone-name"
                      value={zoneForm.name}
                      onChange={(e) =>
                        setZoneForm((s) => ({ ...s, name: e.target.value }))
                      }
                      placeholder="Zone name"
                      disabled={!!editingZone && !isMyZone}
                    />
                  );
                })()}
              </div>
              <div>
                <label
                  htmlFor="business-zone-type"
                  className="text-foreground mb-1 block text-sm font-medium"
                >
                  Type *
                </label>
                {(() => {
                  const isMyZone = isMyZoneForZone(editingZone);
                  const allowed = [1, 2, 4];
                  const isEditingLegacy =
                    !!editingZone && [5].includes(editingZone.type);
                  const baseOptions = typeOptions.filter((o) =>
                    allowed.includes(o.value),
                  );
                  const legacyOption = isEditingLegacy
                    ? typeOptions.find((o) => o.value === editingZone.type)
                    : null;
                  return (
                    <NativeSelect
                      id="business-zone-type"
                      className="h-10 w-full"
                      value={zoneForm.type}
                      onChange={(e) =>
                        setZoneForm((s) => ({
                          ...s,
                          type: Number(e.target.value),
                          data: null,
                        }))
                      }
                      disabled={!!editingZone && !isMyZone}
                    >
                      {legacyOption ? (
                        <option value={legacyOption.value} disabled>
                          {legacyOption.content} (current)
                        </option>
                      ) : null}
                      {baseOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.content}
                        </option>
                      ))}
                    </NativeSelect>
                  );
                })()}
              </div>
              <div>
                <label
                  htmlFor="business-zone-minimum"
                  className="text-foreground mb-1 block text-sm font-medium"
                >
                  Minimum *
                </label>
                <Input
                  id="business-zone-minimum"
                  value={zoneForm.minimum}
                  onChange={(e) =>
                    setZoneForm((s) => ({
                      ...s,
                      minimum: e.target.value.replace(/\D/g, "").slice(0, 15),
                    }))
                  }
                  placeholder="e.g. 1"
                />
              </div>
              <div>
                <label
                  htmlFor="business-zone-price"
                  className="text-foreground mb-1 block text-sm font-medium"
                >
                  Price *
                </label>
                <Input
                  id="business-zone-price"
                  value={zoneForm.price}
                  onChange={(e) =>
                    setZoneForm((s) => ({
                      ...s,
                      price: e.target.value.replace(/\D/g, "").slice(0, 15),
                    }))
                  }
                  placeholder="e.g. 1"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="business-zone-address"
                className="text-foreground mb-1 block text-sm font-medium"
              >
                Business address
              </label>
              <Input
                id="business-zone-address"
                value={businessAddress || ""}
                onChange={() => {}}
                disabled
              />
            </div>

            {Number(zoneForm.type) === 1 ? (
              <div className="space-y-2">
                <label className="text-foreground block text-sm font-medium">
                  Select circle area
                </label>
                <BusinessZoneCircleMap
                  apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                  center={{
                    lat: businessLocation?.lat,
                    lng: businessLocation?.lng,
                  }}
                  value={zoneForm.data}
                  onChange={(val) => setZoneForm((s) => ({ ...s, data: val }))}
                />
              </div>
            ) : null}

            {Number(zoneForm.type) === 2 ? (
              <div className="space-y-2">
                <label className="text-foreground block text-sm font-medium">
                  Draw polygon area
                </label>
                <BusinessZonePolygonMap
                  apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                  center={{
                    lat: businessLocation?.lat,
                    lng: businessLocation?.lng,
                  }}
                  value={zoneForm.data}
                  onChange={(val) => setZoneForm((s) => ({ ...s, data: val }))}
                />
              </div>
            ) : null}

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="ghost"
                onClick={onClose}
                className="border-border bg-secondary text-foreground rounded border px-4 py-2 text-sm"
              >
                Cancel
              </Button>
              {(() => {
                const isType1 = Number(zoneForm.type) === 1;
                const isType2 = Number(zoneForm.type) === 2;
                const polygonData = zoneForm?.data || [];

                const hasValidData =
                  (!isType1 && !isType2) ||
                  (isType1 && zoneForm.data?.center) ||
                  (isType2 && polygonData && polygonData.length >= 3);

                const isDisabled =
                  !zoneForm.name.trim() ||
                  !zoneForm.type ||
                  zoneForm.minimum === "" ||
                  zoneForm.price === "" ||
                  ((isType1 || isType2) && !hasValidData);

                return (
                  <Button
                    variant="default"
                    onClick={onSubmitZone}
                    disabled={isDisabled}
                    className="px-4 py-2 disabled:opacity-50"
                  >
                    {editingZone ? "Save changes" : "Create zone"}
                  </Button>
                );
              })()}
            </div>
          </div>
        ) : null}

        {tab === "share" ? (
          <div className="relative space-y-4">
            {!editingZone ? (
              <div className="text-muted-foreground text-sm">
                Save the zone first to share it with other businesses.
              </div>
            ) : (
              <>
                {/* Search Input */}
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Icon
                      name="Search"
                      className="text-muted-foreground h-5 w-5"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Search businesses by name..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="border-border bg-card focus:ring-ring h-10 w-full rounded-lg border px-4 py-2 pr-10 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:outline-none"
                  />
                  {searchInput && (
                    <Button
                      variant="ghost"
                      onClick={() => setSearchInput("")}
                      className="bg-muted hover:bg-muted absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full transition-colors"
                      type="button"
                    >
                      <svg
                        className="text-foreground h-3 w-3 hover:text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </Button>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-foreground text-sm">
                    Toggle businesses to share this zone
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      onClick={handleSelectAll}
                      className="text-primary hover:text-primary flex items-center space-x-1 px-3 py-2 text-sm font-medium transition-colors"
                    >
                      <Icon name="CheckSquare" className="h-4 w-4" />
                      <span>Select All</span>
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleSelectNone}
                      className="text-muted-foreground hover:text-foreground flex items-center space-x-1 px-3 py-2 text-sm font-medium transition-colors"
                    >
                      <Icon name="Square" className="h-4 w-4" />
                      <span>Select None</span>
                    </Button>
                  </div>
                </div>

                <div className="border-border bg-card/60 overflow-hidden rounded-xl border">
                  {isLoadingBusinesses ? (
                    <div className="text-foreground p-6 text-center">
                      <div className="border-primary mx-auto mb-2 h-6 w-6 animate-spin rounded-full border-b-2"></div>
                      Loading businesses...
                    </div>
                  ) : filteredBusinesses.length === 0 ? (
                    <div className="text-foreground p-6 text-center">
                      No businesses found
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-700">
                      {filteredBusinesses.map((b) => {
                        const checked = selectedBusinessIds.has(b.id);
                        return (
                          <div
                            key={b.id}
                            className="flex items-center justify-between px-4 py-3"
                          >
                            <div className="flex items-center space-x-3">
                              {b.logo ? (
                                <img
                                  src={b.logo}
                                  alt={b.name}
                                  className="h-8 w-8 rounded object-cover"
                                />
                              ) : (
                                <div className="bg-secondary flex h-8 w-8 items-center justify-center rounded">
                                  <Icon
                                    name="Store"
                                    className="text-muted-foreground h-4 w-4"
                                  />
                                </div>
                              )}
                              <div className="text-sm text-white">{b.name}</div>
                            </div>
                            <Switch
                              checked={checked}
                              onCheckedChange={(v) =>
                                toggleBusinessSelected(b.id, v)
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="sticky bottom-0 z-10">
                  <div className="border-border bg-background/80 -mx-4 mt-4 flex justify-end border-t px-4 py-3 backdrop-blur">
                    <Button
                      variant="default"
                      onClick={async () => {
                        if (!editingZone || savingShare) return;
                        try {
                          setSavingShare(true);
                          await onUpdateZoneBusinesses?.(
                            editingZone.id,
                            Array.from(selectedBusinessIds),
                          );
                        } finally {
                          setSavingShare(false);
                        }
                      }}
                      disabled={savingShare}
                      className="flex items-center justify-center px-4 py-2 disabled:opacity-50"
                    >
                      {savingShare ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                      ) : (
                        "Save sharing"
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default BusinessZoneModal;
